import { api, type EndpointProbeRequest, type ProbeReport } from "@/lib/api";
import { describeModelError } from "@/lib/model-errors";
import { useEffect, useRef, useState } from "react";

const POLL_MS = 1500;
// A test the Boo never finishes stops being polled after this long.
const GIVE_UP_MS = 3 * 60 * 1000;

// Starts a fitness test of an org endpoint and polls it until it completes.
// The test runs on one of the org's running Boos (`ranOn`). A failure while
// polling (an expired test after that Boo restarted, a network error) or the
// time limit ends the test with an error, so "Run again" is available.
export function useProbe(opts: { onComplete?: (report: ProbeReport) => void } = {}) {
  const [report, setReport] = useState<ProbeReport | null>(null);
  const [ranOn, setRanOn] = useState<string | null>(null);
  // The handle the backend gave this check, and what it checked; a save can
  // pass it on so the result is kept.
  const [checked, setChecked] = useState<{ probeId: string; request: EndpointProbeRequest } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [starting, setStarting] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout>>(undefined);
  const generation = useRef(0);
  const onComplete = useRef(opts.onComplete);
  useEffect(() => {
    onComplete.current = opts.onComplete;
  });

  useEffect(() => () => clearTimeout(timer.current), []);

  const fail = (message: string) => {
    setReport(null);
    setError(message);
  };

  const poll = (probeId: string, gen: number, startedAt: number) => {
    timer.current = setTimeout(async () => {
      if (gen !== generation.current) return;
      if (Date.now() - startedAt > GIVE_UP_MS) {
        fail("The check is taking too long. Run it again.");
        return;
      }
      try {
        const next = await api.models.getEndpointProbe(probeId);
        if (gen !== generation.current) return;
        setReport(next);
        if (next.status === "running") poll(probeId, gen, startedAt);
        else onComplete.current?.(next);
      } catch (err) {
        if (gen !== generation.current) return;
        fail(describeModelError(err, { defs: [] }));
      }
    }, POLL_MS);
  };

  const start = async (body: EndpointProbeRequest) => {
    clearTimeout(timer.current);
    const gen = ++generation.current;
    setError(null);
    setReport(null);
    setRanOn(null);
    setChecked(null);
    setStarting(true);
    try {
      const { probeId, booName } = await api.models.probeEndpoint(body);
      if (gen !== generation.current) return;
      setRanOn(booName);
      setChecked({ probeId, request: body });
      setReport({ probeId, status: "running", checks: [] });
      poll(probeId, gen, Date.now());
    } catch (err) {
      if (gen !== generation.current) return;
      setError(describeModelError(err, { defs: [] }));
    } finally {
      if (gen === generation.current) setStarting(false);
    }
  };

  const reset = () => {
    clearTimeout(timer.current);
    generation.current++;
    setReport(null);
    setRanOn(null);
    setChecked(null);
    setError(null);
    setStarting(false);
  };

  return { report, ranOn, checked, error, starting, running: starting || report?.status === "running", start, reset };
}
